/* The Next Turn — theme behaviour. No dependencies. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- header */

  function initHeader() {
    var hdr = document.querySelector('[data-header]');
    if (!hdr) return;

    var onScroll = function () {
      hdr.classList.toggle('is-stuck', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    var toggle = hdr.querySelector('[data-drawer-toggle]');
    var drawer = hdr.querySelector('[data-drawer]');
    if (!toggle || !drawer) return;

    toggle.addEventListener('click', function () {
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      drawer.classList.toggle('is-open', !open);
    });

    drawer.addEventListener('click', function (e) {
      if (e.target.closest('a')) {
        toggle.setAttribute('aria-expanded', 'false');
        drawer.classList.remove('is-open');
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        toggle.setAttribute('aria-expanded', 'false');
        drawer.classList.remove('is-open');
        toggle.focus();
      }
    });
  }

  /* ------------------------------------------------------------ accordions */
  /* Used by the FAQ and by the "what's inside" chapter parts.               */

  function initAccordions(root) {
    (root || document).querySelectorAll('[data-acc-trigger]').forEach(function (btn) {
      if (btn.dataset.accBound) return;
      btn.dataset.accBound = '1';

      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;

      // set the starting height so the first open animates from 0
      if (btn.getAttribute('aria-expanded') === 'true') {
        panel.style.height = 'auto';
      }

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        var group = btn.closest('[data-acc-group]');

        if (!open && group && group.dataset.accGroup === 'single') {
          group.querySelectorAll('[data-acc-trigger][aria-expanded="true"]').forEach(function (other) {
            if (other === btn) return;
            var op = document.getElementById(other.getAttribute('aria-controls'));
            other.setAttribute('aria-expanded', 'false');
            if (op) collapse(op);
          });
        }

        btn.setAttribute('aria-expanded', String(!open));
        open ? collapse(panel) : expand(panel);
      });
    });

    function expand(panel) {
      if (reduceMotion) { panel.style.height = 'auto'; return; }
      var target = panel.scrollHeight;
      panel.style.height = target + 'px';
      panel.addEventListener('transitionend', function done(e) {
        if (e.propertyName !== 'height') return;
        panel.style.height = 'auto';
        panel.removeEventListener('transitionend', done);
      });
    }

    function collapse(panel) {
      if (reduceMotion) { panel.style.height = '0px'; return; }
      panel.style.height = panel.scrollHeight + 'px';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { panel.style.height = '0px'; });
      });
    }
  }

  /* ------------------------------------------------------- review carousel */
  /* Shows N cards at a time (3 desktop / 2 tablet / 1 phone) and advances a
     whole page at a time, so the buyer always sees a fresh set of three.    */

  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach(function (rev) {
      var track = rev.querySelector('[data-carousel-track]');
      if (!track) return;

      var prev  = rev.querySelector('[data-carousel-prev]');
      var next  = rev.querySelector('[data-carousel-next]');
      var bar   = rev.querySelector('[data-carousel-bar]');
      var count = rev.querySelector('[data-carousel-count]');
      var cards = Array.prototype.slice.call(track.children);
      var total = cards.length;
      if (!total) return;

      function perView() {
        var v = parseInt(getComputedStyle(rev).getPropertyValue('--cards'), 10);
        return isNaN(v) || v < 1 ? 1 : v;
      }

      function stride() {
        if (cards.length < 2) return cards[0].offsetWidth;
        // distance between two card starts = card width + gap
        return Math.round(cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left);
      }

      function maxScroll() {
        return track.scrollWidth - track.clientWidth;
      }

      function update() {
        var n   = perView();
        var s   = stride() || 1;
        var max = maxScroll();
        var at  = track.scrollLeft;

        if (prev) prev.disabled = at <= 2;
        if (next) next.disabled = at >= max - 2;

        var pct = max > 0 ? Math.min(1, Math.max(0, at / max)) : 1;
        if (bar) {
          // the bar fills proportionally to how much of the set has been seen
          var seen = max > 0 ? (n + (total - n) * pct) / total : 1;
          bar.style.width = (seen * 100).toFixed(2) + '%';
        }

        if (count) {
          var first = Math.round(at / s) + 1;
          var last  = Math.min(total, first + n - 1);
          if (at >= max - 2) { last = total; first = Math.max(1, total - n + 1); }
          count.textContent = 'Showing ' + first + '–' + last + ' of ' + total + ' reviews';
        }
      }

      function page(dir) {
        var amount = stride() * perView();
        track.scrollBy({ left: dir * amount, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      if (prev) prev.addEventListener('click', function () { page(-1); });
      if (next) next.addEventListener('click', function () { page(1); });

      var raf;
      track.addEventListener('scroll', function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(update);
      }, { passive: true });

      window.addEventListener('resize', function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(update);
      }, { passive: true });

      // keyboard support on the track itself
      track.setAttribute('tabindex', '0');
      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); page(1); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); page(-1); }
      });

      update();
    });
  }

  /* -------------------------------------------------------- product gallery */

  function initGallery() {
    document.querySelectorAll('[data-gallery]').forEach(function (gal) {
      var main   = gal.querySelector('[data-gallery-main]');
      var thumbs = gal.querySelectorAll('[data-gallery-thumb]');
      if (!main || !thumbs.length) return;

      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          thumbs.forEach(function (o) { o.setAttribute('aria-current', 'false'); });
          t.setAttribute('aria-current', 'true');
          var full = t.getAttribute('data-full');
          var alt  = t.getAttribute('data-alt') || '';
          if (full) {
            main.src = full;
            main.removeAttribute('srcset');
            main.alt = alt;
          }
        });
      });
    });
  }

  /* --------------------------------------------------------- smooth anchors */

  function initAnchors() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (!id || id === '#' || id.length < 2) return;
      var target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      var hdr = document.querySelector('[data-header]');
      var offset = (hdr ? hdr.offsetHeight : 0) + 16;
      var top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  }

  /* ------------------------------------------------------------------- boot */

  function boot() {
    initHeader();
    initAccordions();
    initCarousels();
    initGallery();
    initAnchors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // re-bind when a section is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    initAccordions(e.target);
    initCarousels();
    initGallery();
    initHeader();
  });
})();
